import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
/***** Configs *****/
import { AppConfig } from 'src/app/app.config';
/***** Rxjs *****/
import { BehaviorSubject, from, Observable, of, Subject, TimeoutError } from 'rxjs';
import { catchError, map, mergeMap, retry, timeout, take, takeUntil } from 'rxjs/operators';
/***** Services *****/
import { AlertService } from './alert.service';
import { DateService } from './date.service';
import { GlobalService } from './global.service';
import { MytoastrService } from './mytoastr.service';
import { StorageService } from './storage.service';

export interface ApiResponse {
  success: boolean;
  code: number;
  data: any;
  err_custom?: any, // Server錯誤訊息(自定義)
  err_system?: any, // Server錯誤訊息(系統訊息)
}
interface ApiRequestMark {
  ip?: string;
  tw_time?: string;
}
interface ApiRequestSelect extends ApiRequestMark {
  sql_table: string;
  sql_columns: Array<string>;
  sql_condition: string;
  isOmitToken?: boolean; // 使否省略驗證token
}
interface ApiRequestDelete extends ApiRequestMark {
  sql_table: string;
  sql_columns: Array<string>;
  sql_value: Array<any>;
  sql_condition: string;
}
export interface ApiBlob1to1_Img_MutipleArray {
  textCommon: {},
  textArray: any[],
  imageArray: File[]
}
export interface ApiUploadFile {
  textObj: {},
  filePath: string;
  fileArray: File[]
}

/**** 修改參數 *****/
export interface RegistrationInfo {

}
export interface AccountInfo {
  accountNo: string; // smi17 // varchar(8) // 帳號編號
  userNo: string; // smi03 // varchar(10) // 使用者編號
  userName: string; // smi04 // varchar(10) // 使用者名稱
  userRole: number; // smi16 // int // 使用者身分 // 0臨時帳號/1會員/2員工/3廠商/4管理員/99最高管理員(革雲)
  firstLogin: number; // smi106 // int // 是否第一次登入
}
export interface UserInfo {
  userSerail: number; // 
  userNo: string; // 
  userName: string; //
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {  

  /***** Params *****/
  userFirstLogin: boolean = false;

  /***** Rxjs Behaviors *****/
  private login$ = new BehaviorSubject<boolean>(false);
  private accountInfo$ = new BehaviorSubject<AccountInfo|null>(null);
  private userInfo$ = new BehaviorSubject<any|null>(null);

  /***** Api *****/
  private apiTimeout: number = 30000; // 30秒

  constructor(
    private http: HttpClient,
    /***** Services *****/
    private alertService: AlertService,
    private dateService: DateService,
    private globalService: GlobalService,
    private mytoastrService: MytoastrService,
    private storageService: StorageService,
  ) { }

  /***** 登入/註冊(修改參數) *****/
  signin(account: string, password: string): Observable<boolean> {
    return this.api_p({ account, password }, 'auth/signin')
      .pipe(
        map(res => {
          if (res.success) {
            const authToken = res.data.token;
            const accountInfo: AccountInfo = res.data.accountInfo;
            const userInfo: UserInfo = res.data.userInfo;

            if (accountInfo.userRole === 0) {
              this.userFirstLogin = accountInfo.firstLogin === 0 ? true : false;
            }
            
            /* 設置 Token */
            this.storageService.setToken(authToken);
            /* 設置 帳號資料 */
            this.setAccountInfo(accountInfo).subscribe();
            /* 設置 使用者資料 */
            this.setUserInfo(userInfo).subscribe();
            /* 設置 登入狀態 */ 
            this.setLoginStatus(true);
            
            return true;
          } else {
            return false;
          }
        })
      );
  }
  signup(registrationInfo: RegistrationInfo): Observable<boolean> {
    const payload = {};
    return this.api_p(payload, 'auth/signup')
      .pipe(
        map(res => {
          if (res.success) {
            let authToken = res.data.token;
            let userInfo = res.data.userInfo;

            /* 設置 Token */
            this.storageService.setToken(authToken);
            /* 設置 使用者資料 */
            // this.setUserProfileInfo({}).pipe(take(1)).subscribe();
            /* 設置 登入狀態 */ 
            this.setLoginStatus(true);

            return true;
          } else {
            return false;
          }

        }),
      )
  }

  /***** 登入狀態 *****/
  getLoginStatus(): Observable<boolean> {
    return this.login$;
  }
  setLoginStatus(isLogin: boolean): void {
    this.login$.next(isLogin);
    if (isLogin) {
      this.mytoastrService.success_login();
    } else {
      this.storageService.removeToken();
      this.storageService.removeUserRole();
      this.setAccountInfo(null);
      this.setUserInfo(null);
    }
  }

  /***** 帳號資料 *****/
  getAccountInfo(): Observable<AccountInfo|null> {
    return this.accountInfo$;
  }
  setAccountInfo(value: AccountInfo|null): Observable<AccountInfo|null> {
    if (value) this.storageService.setUserRole(value.userRole);
    this.accountInfo$.next(value);
    return this.getAccountInfo().pipe(take(1));
  }

  /***** 使用者資料 *****/
  getUserInfoFromServer(): Observable<UserInfo|null> {
    return this.api_p({}, 'auth/get/userinfo')
      .pipe(
        mergeMap((res) => {
          if (res.success) {
            const responseData = res.data;

            if (responseData.accountInfo.userRole === 0) {
              this.userFirstLogin = responseData.accountInfo.firstLogin === 0 ? true : false;
            }

            /* 設置 使用者資料 */
            this.setUserInfo(responseData.userInfo);
            /* Account User Info */
            this.setAccountInfo(responseData.accountInfo);

            /* Account Permission Info */
            /* User Favorite */
            /* User Info */


            return of(null);
          } else {
            return of(null);
          }
        }),
        map(res => {
          return res;
        })
      );
  }
  getUserInfo(): Observable<UserInfo|null> {
    return this.userInfo$;
  }
  setUserInfo(value: UserInfo|null): Observable<UserInfo|null> {
    this.userInfo$.next(value);
    return this.getUserInfo().pipe(take(1));
  }

  /***** Auth Api *****/
  api_r(payload: ApiRequestSelect): Observable<ApiResponse> {
    payload.tw_time = this.dateService.YYYYMMDDHHMMSS('TW', new Date());
    return this.apiPost('table/select', payload)
      .pipe(
        map((e: ApiResponse) => {
          if (e.success) {

          } else {

          }
          return e;
        })
      );
  }
  api_d(payload: ApiRequestDelete): Observable<ApiResponse> {
    return this.accountInfo$
      .pipe(
        mergeMap((accountInfo) => {
          payload['userNo'] = accountInfo ? accountInfo.userNo : '';
          payload['userName'] = accountInfo ? accountInfo.userName : '';
          return this.apiPost('table/delete', payload);
        }),
        take(1),
        map((e: ApiResponse) => {
          if (e.success) {

          } else {

          }
          return e;
        })
      );
  }
  api_p(payload: any, endpoint: string): Observable<ApiResponse> {
    return this.accountInfo$
      .pipe(
        mergeMap((accountInfo) => {
          payload['accountNo'] = accountInfo ? accountInfo.accountNo : '';
          payload['userNo'] = accountInfo ? accountInfo.userNo : '';
          payload['userName'] = accountInfo ? accountInfo.userName : '';
          return this.apiPost(endpoint, payload);
        }),
        take(1),
        map((e: ApiResponse) => {
          if (e.success) {

          } else {
            this.mytoastrService.error(e.code, e);
          }
          return e;
        })
      );
  }

  private apiPost(endpoint: string, value: any): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(AppConfig.apiUrl + '/api/' + endpoint, value)
      .pipe(
        retry(2), // 重新請求兩次(總共3次)
        timeout(this.apiTimeout),
        catchError(returnedError => {
          // console.log('Call API failed::', returnedError);
          console.log(`Call API failed::${returnedError.status} ${returnedError.statusText}`);

          if (returnedError instanceof TimeoutError) { // Rxjs Timeout 錯誤
            // 請求3次都失敗，總秒數大於 apiTimeout
            this.alertService.timeoutError();
            return of(<ApiResponse>{ // 網路連線逾時
              success: false,
              code: 112,
              data: [],
              err_custom: '',
              err_system: returnedError,
            });
          } else { // 其他錯誤
            if (returnedError instanceof HttpErrorResponse) {
              this.alertService.apiError();
              return of(<ApiResponse>{ // API請求錯誤
                success: false,
                code: 113,
                data: [],
                err_custom: '',
                err_system: returnedError,
              });
            } else {
              this.alertService.unknownError();
              return of(<ApiResponse>{
                success: false,
                code: 999,
                data: [],
                err_custom: 'unknown error',
                err_system: returnedError,
              });
            }
          }

        })
      );
  }
}

